import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Modal, Form, ButtonGroup, Button } from 'react-bootstrap'
import { toast } from '@CommonComponents/toast'
import { client, MAX_LATE_DEDUCTIONS } from '@Utils'
import { useStateValue } from '@State'

export const EditContextModal = ({
  show,
  toggle,
  context,
  issuerid,
  clientid,
  deploymentid,
  contextid,
}) => {
  const [, dispatch] = useStateValue()
  const [stateObj, setStateObj] = useState(context)

  // UI state for the rows in the deductions table
  const [lateDeductions, setLateDeductions] = useState([])

  // --- helpers for parsing/serializing late deductions ---

  const parseLateDeductions = raw => {
    if (!raw) return []

    var arr = raw

    // If it's a string like "[[1,'d',100.0,'percent']]"
    if (typeof arr === 'string') {
      try {
        // Convert single quotes to double quotes for JSON.parse
        var jsonStr = arr.replace(/'/g, '"')
        arr = JSON.parse(jsonStr)
      } catch (e) {
        console.warn('Could not parse defaultLatePolicyDeductions:', raw, e)
        return []
      }
    }

    if (!Array.isArray(arr)) return []

    return arr.map(function (inner) {
      inner = Array.isArray(inner) ? inner : []

      return {
        offset:
          inner.length > 0 && inner[0] !== null && inner[0] !== undefined
            ? inner[0]
            : '',
        unit:
          inner.length > 1 && inner[1] !== null && inner[1] !== undefined
            ? inner[1]
            : 'd',
        amount:
          inner.length > 2 && inner[2] !== null && inner[2] !== undefined
            ? inner[2]
            : '',
        mode:
          inner.length > 3 && inner[3] !== null && inner[3] !== undefined
            ? inner[3]
            : 'percent',
      }
    })
  }

  const serializeLateDeductions = rows => {
    if (!rows || !rows.length) return ''

    // Only include rows that have both an offset and amount
    const inner = rows
      .filter(r => r.offset !== '' && r.amount !== '')
      .map(r => {
        const offset = Number(r.offset) || 0
        const amount = Number(r.amount) || 0
        const unit = r.unit || 'd'
        const mode = r.mode || 'percent'
        return `[${offset},'${unit}',${amount},'${mode}']`
      })

    if (!inner.length) return ''
    return `[${inner.join(',')}]`
  }

  const syncLateDeductionsToStateObj = updatedRows => {
    const serialized = serializeLateDeductions(updatedRows)

    setLateDeductions(updatedRows)

    setStateObj(prev => {
      const prevSnap = prev.snap || {}

      return {
        ...prev,
        snap: {
          ...prevSnap,
          defaultLatePolicyDeductions: serialized,
        },
      }
    })
  }

  const handleLateDeductionChange = (index, field, value) => {
    const updated = lateDeductions.map((row, i) =>
      i === index ? { ...row, [field]: value } : row
    )
    syncLateDeductionsToStateObj(updated)
  }

  const addLateDeductionRow = () => {
    if (lateDeductions.length >= MAX_LATE_DEDUCTIONS) return
    const updated = [
      ...lateDeductions,
      { offset: '1', unit: 'd', amount: '100.0', mode: 'percent' },
    ]
    syncLateDeductionsToStateObj(updated)
  }

  const removeLateDeductionRow = index => {
    const updated = lateDeductions.filter((_, i) => i !== index)
    syncLateDeductionsToStateObj(updated)
  }

  useEffect(() => {
    if (!context) return

    const baseSnap = context.snap || {
      scoreDecisionHandler: '',
      dueDateHandler: '',
      submissionLimitHandler: '',
      defaultLatePolicy: '',
      defaultLatePolicyDeductions: '',
    }

    const newStateObj = {
      ...context,
      snap: {
        ...baseSnap,
      },
    }

    setStateObj(newStateObj)

    const parsed = parseLateDeductions(
      baseSnap.defaultLatePolicyDeductions || ''
    )

    if (!parsed.length) {
      console.error(
        'Could not parse the deductions! UI likely will not report correctly.'
      )
    }

    setLateDeductions(parsed.length ? parsed : [])
  }, [context])

  const commitStateObject = async () => {
    try {
      // switch data for mydata for update; See Item structure in dserv project
      const fullStateObj = { ...stateObj }

      const resp = await client.post(
        `/account/admin_lti13.editContext/-/${issuerid}/${clientid}/${deploymentid}/${contextid}`,
        fullStateObj
      )
      toast.success('Successfully edited the context.')
      dispatch({ type: 'updateContext', context: resp.data.context })
    } catch (err) {
      console.log(err)
      let errMsg = 'There was an error trying to update the Platform Instance.'
      if (err.response) {
        errMsg = `Error: ${err.response.data.error}`
      }
      toast.error(errMsg)
    } finally {
      toggle(!show)
    }
  }

  const handleChange = e => {
    const { name, type, checked, value } = e.target

    setStateObj(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }))
  }

  const handleSnapChange = e => {
    const { name, type, checked, value } = e.target

    setStateObj(prev => {
      const prevSnap = prev.snap || {}
      return {
        ...prev,
        snap: {
          ...prevSnap,
          [name]: type === 'checkbox' ? checked : value,
        },
      }
    })
  }

  const helpTextStyle = {
    fontSize: '0.8em',
    color: '#6B5400',
  }

  return (
    <Modal show={show} onHide={() => toggle(!show)}>
      <Modal.Header closeButton>
        <Modal.Title>Edit Context Instance</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group controlId="title">
          <Form.Label>Title:</Form.Label>
          <Form.Control
            type="text"
            onChange={handleChange}
            name="title"
            value={
              stateObj && stateObj.contextTitle ? stateObj.contextTitle : ''
            }
          />
        </Form.Group>
        <Form.Group controlId="contextpk">
          <Form.Label>Context PK:</Form.Label>
          <Form.Control
            type="text"
            onChange={handleChange}
            name="contextpk"
            disabled={true}
            value={stateObj && stateObj.id ? stateObj.id : ''}
          />
        </Form.Group>

        <Form.Group controlId="contextid">
          <Form.Label>Context ID:</Form.Label>
          <Form.Control
            type="text"
            onChange={handleChange}
            name="contextid"
            disabled={true}
            value={stateObj && stateObj.contextId ? stateObj.contextId : ''}
          />
        </Form.Group>

        <Form.Group controlId="defaultLatePolicy">
          <Form.Label>Default Late Policy:</Form.Label>
          <Form.Control
            as="select"
            onChange={handleSnapChange}
            name="defaultLatePolicy"
            value={
              stateObj &&
              stateObj.snap &&
              typeof stateObj.snap.defaultLatePolicy === 'string'
                ? stateObj.snap.defaultLatePolicy
                : ''
            }
          >
            <option value="">No Policy</option>
            <option value="flag">Flag</option>
            <option value="prevent">Prevent</option>
            <option value="deduct">Deduct</option>
          </Form.Control>
          <div style={helpTextStyle}>
            Flag as late only, prevent submissions, or deduct points according
            to deductions
          </div>
        </Form.Group>

        <Form.Group controlId="defaultLatePolicyDeductions">
          <Form.Label>Default Late Policy Deductions:</Form.Label>

          {/* Column headers */}
          <div
            className="d-flex mb-1"
            style={{ fontSize: '0.8em', fontWeight: 'bold' }}
          >
            <div style={{ flex: 1 }}>After</div>
            <div style={{ width: '30%' }}>Unit</div>
            <div style={{ flex: 1 }}>Amount</div>
            <div style={{ width: '110px' }}>Type</div>
            <div style={{ width: '40px' }} />
          </div>

          {/* Rows */}
          {lateDeductions.map((row, index) => (
            <div className="d-flex mb-2" key={index}>
              {/* Offset (int) */}
              <Form.Control
                type="number"
                min="0"
                step="1"
                placeholder="1"
                value={row.offset}
                onChange={e =>
                  handleLateDeductionChange(index, 'offset', e.target.value)
                }
                style={{ flex: 1, marginRight: '4px' }}
              />

              {/* Unit: d/h/m */}
              <Form.Control
                as="select"
                value={row.unit}
                onChange={e =>
                  handleLateDeductionChange(index, 'unit', e.target.value)
                }
                style={{ width: '30%', marginRight: '4px' }}
              >
                <option value="d">days (d)</option>
                <option value="h">hours (h)</option>
                <option value="m">minutes (m)</option>
              </Form.Control>

              {/* Amount (decimal) */}
              <Form.Control
                type="number"
                step="0.01"
                placeholder="100"
                value={row.amount}
                onChange={e =>
                  handleLateDeductionChange(index, 'amount', e.target.value)
                }
                style={{ flex: 1, marginRight: '4px' }}
              />

              {/* Mode: points/percent */}
              <Form.Control
                as="select"
                value={row.mode}
                onChange={e =>
                  handleLateDeductionChange(index, 'mode', e.target.value)
                }
                style={{ width: '110px', marginRight: '4px' }}
              >
                <option value="points">points</option>
                <option value="percent">percent</option>
              </Form.Control>

              {/* Remove row */}
              <button
                type="button"
                className="btn btn-link btn-sm text-danger"
                onClick={() => removeLateDeductionRow(index)}
                title="Remove this deduction"
              >
                &times;
              </button>
            </div>
          ))}

          {/* Add row button */}
          {lateDeductions.length < MAX_LATE_DEDUCTIONS && (
            <button
              type="button"
              className="btn btn-link btn-sm p-0 mb-2"
              onClick={addLateDeductionRow}
            >
              + Add deduction
            </button>
          )}

          <div style={helpTextStyle}>
            Nested array structure of late penalties to be applied as a default.
            Only used if Default Late Policy is set to Deduct. Each row becomes
            an array like [int, &apos;d/h/m&apos;, decimal,
            &apos;points/percent&apos;]. Example: after 1 day, deduct 100
            percent → [1, &apos;d&apos;, 100.0, &apos;percent&apos;].
          </div>
        </Form.Group>

        <Form.Group controlId="dueDateHandler">
          <Form.Label>Due Date Handler:</Form.Label>
          <Form.Control
            as="select"
            onChange={handleSnapChange}
            name="dueDateHandler"
            value={
              stateObj &&
              stateObj.snap &&
              typeof stateObj.snap.dueDateHandler === 'string'
                ? stateObj.snap.dueDateHandler
                : ''
            }
          >
            <option value="">No handler</option>
            <option value="base">Base</option>
            <option value="ags">AGS Lookup</option>
            <option value="canvas">Canvas</option>
            <option value="brightspace">Brightspace</option>
          </Form.Control>

          <div style={helpTextStyle}>
            The handler for how to get the due date for a launched assessment.
            Base handler currently does not do anything. AGS uses the AGS API to
            get the endDateTime from the LMS. Canvas extends AGS and also
            supports student-specific due dates via custom data. Brightspace
            reads the due date from custom data, since Brightspace does not
            send it through AGS.
          </div>
        </Form.Group>

        <Form.Group controlId="scoreDecisionHandler">
          <Form.Label>Score Decision Handler:</Form.Label>
          <Form.Control
            as="select"
            onChange={handleSnapChange}
            name="scoreDecisionHandler"
            value={
              stateObj &&
              stateObj.snap &&
              typeof stateObj.snap.scoreDecisionHandler === 'string'
                ? stateObj.snap.scoreDecisionHandler
                : ''
            }
          >
            <option value="">No handler</option>
            <option value="base">Normal</option>
            <option value="4_days_late">4 day limit</option>
          </Form.Control>

          <div style={helpTextStyle}>
            Select which submission handler to apply for this context. This
            determines if a score should be sent to the LMS or not
          </div>
        </Form.Group>

        <Form.Group controlId="submissionLimitHandler">
          <Form.Label>Submission Limit Handler:</Form.Label>
          <Form.Control
            as="select"
            onChange={handleSnapChange}
            name="submissionLimitHandler"
            value={
              stateObj &&
              stateObj.snap &&
              typeof stateObj.snap.submissionLimitHandler === 'string'
                ? stateObj.snap.submissionLimitHandler
                : ''
            }
          >
            <option value="">No handler</option>
            <option value="base">Base</option>
            <option value="canvas">Canvas</option>
          </Form.Control>

          <div style={helpTextStyle}>
            The handler for how to get the submission limit for a launched
            assessment. Base handler currently does not do anything. Canvas reads
            the allowed attempts from custom data sent during the LTI launch.
          </div>
        </Form.Group>

        <Form.Group controlId="embeddedAssessmentLinks">
          <Form.Label>Embedded Assessment Links:</Form.Label>
          <Form.Control
            as="select"
            name="embeddedAssessmentLinks"
            value={
              typeof stateObj?.snap?.embeddedAssessmentLinks === 'boolean'
                ? String(stateObj.snap.embeddedAssessmentLinks)
                : ''
            }
            onChange={e => {
              const { value } = e.target
              // '' follows the deployment: editContext drops the key
              setStateObj(prev => ({
                ...prev,
                snap: {
                  ...(prev.snap || {}),
                  embeddedAssessmentLinks: value === '' ? '' : value === 'true',
                },
              }))
            }}
          >
            <option value="">Follow the deployment</option>
            <option value="true">On</option>
            <option value="false">Off</option>
          </Form.Control>

          <div style={helpTextStyle}>
            When on, an assessment embedded in a selected reading section
            replaces that section&apos;s reading link in deep links, embedded
            assessments open in the reading, and links are sent in book order.
            On or Off overrides the deployment&apos;s setting for this context.
          </div>
        </Form.Group>

        <Form.Group controlId="duplication_finished">
          <Form.Label>Duplication Finished:</Form.Label>
          <div>
            <ButtonGroup>
              <Button
                variant={stateObj && stateObj.duplication_finished === true ? 'primary' : 'outline-secondary'}
                onClick={() =>
                  setStateObj(prev => ({ ...prev, duplication_finished: true }))
                }
              >
                True
              </Button>
              <Button
                variant={stateObj && stateObj.duplication_finished === false ? 'primary' : 'outline-secondary'}
                onClick={() =>
                  setStateObj(prev => ({ ...prev, duplication_finished: false }))
                }
              >
                False
              </Button>
            </ButtonGroup>
          </div>
          <div style={helpTextStyle}>
            Whether course duplication has finished for this context. Set to
            False if the context is stuck and needs to be reset.
          </div>
        </Form.Group>

        <div
          className="btn btn-success btn-myed btn-success-myed"
          onClick={() => commitStateObject()}
        >
          <div className="fa-regular fa-plus fa-fw mr-2" /> Update
        </div>
      </Modal.Body>
    </Modal>
  )
}

EditContextModal.propTypes = {
  show: PropTypes.bool.isRequired,
  toggle: PropTypes.func.isRequired,
  context: PropTypes.object,
}
